import { useState } from 'react';
import {
    Stack,
    InlineNotification,
    Heading,
    Link,
    Loading,
    Toggletip,
    ToggletipButton,
    ToggletipContent,
} from '@carbon/react';
import {
    Button,
    PasswordInput,
    TextInput,
} from '@mantine/core';
import { Information } from '@carbon/icons-react';
import type { IntlShape } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import styles from './RegisterScreen.module.scss';

import { useRegisterMutation } from 'api/accountEndpoints';

const isEmailValid = (email: string) => /\S+@\S+\.\S+/.test(email);

const getPasswordRequirements = (formatMessage: IntlShape['formatMessage']) => [
    {
        key: 'minLength',
        label: formatMessage(messages.passwordRequirementMinLength),
        isValid: (password: string) => password.length >= 6,
    },
    {
        key: 'digit',
        label: formatMessage(messages.passwordRequirementDigit),
        isValid: (password: string) => /\d/.test(password),
    },
    {
        key: 'lowercase',
        label: formatMessage(messages.passwordRequirementLowercase),
        isValid: (password: string) => /[a-z]/.test(password),
    },
    {
        key: 'uppercase',
        label: formatMessage(messages.passwordRequirementUppercase),
        isValid: (password: string) => /[A-Z]/.test(password),
    },
];

const getPasswordValidationMessage = (password: string, formatMessage: IntlShape['formatMessage']) => {
    const missingRequirements = getPasswordRequirements(formatMessage)
        .filter((requirement) => !requirement.isValid(password))
        .map((requirement) => requirement.label);

    if (!missingRequirements.length) {
        return '';
    }

    return formatMessage(messages.passwordRequirementsError, {
        requirements: missingRequirements.join('; '),
    });
};

const RegisterScreen = () => {
    const { formatMessage } = useIntl();
    const [register, { isLoading }] = useRegisterMutation();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const navigate = useNavigate();
    const passwordRequirements = getPasswordRequirements(formatMessage);
    const passwordValidationMessage = getPasswordValidationMessage(password, formatMessage);
    const shouldShowPasswordValidation = hasAttemptedSubmit && Boolean(passwordValidationMessage);
    const passwordsMismatch = password !== confirmPassword;
    const shouldShowConfirmPasswordValidation = hasAttemptedSubmit
        && Boolean(confirmPassword)
        && passwordsMismatch;
    const isSubmitDisabled = isLoading
        || !firstName.trim()
        || !lastName.trim()
        || !isEmailValid(email)
        || password.length < 6
        || !confirmPassword
        || shouldShowPasswordValidation
        || shouldShowConfirmPasswordValidation;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setHasAttemptedSubmit(true);

        if (passwordValidationMessage) {
            return;
        }

        if (passwordsMismatch) {
            return;
        }

        if (firstName.length < 3) {
            setError(formatMessage(messages.incorrectFirstName));
            return;
        }

        if (lastName.length < 3) {
            setError(formatMessage(messages.incorrectLastName));
            return;
        }

        const result = await register({
            email,
            password,
            firstName,
            lastName,
        });

        if ('error' in result) {
            setError(formatMessage(messages.accountCreationFailed));
        } else {
            navigate('/confirmation');
        }
    };

    return (
        <div className={styles.container}>
            {isLoading && <Loading />}
            <Stack className={styles.stack} gap={6}>
                <div>
                    <Heading className={styles.heading}>{formatMessage(messages.createAccountTitle)}</Heading>
                    <p className={styles.subtitle}>{formatMessage(messages.createAccountSubtitle)}</p>
                </div>

                {error && (
                    <InlineNotification
                        kind="error"
                        title={formatMessage(messages.errorTitle)}
                        subtitle={error}
                        onClose={() => { setError(''); }}
                        lowContrast
                    />
                )}

                <form onSubmit={handleSubmit}>
                    <Stack gap={5}>
                        <TextInput
                            id="email"
                            classNames={{
                                root: styles.fieldRoot,
                                label: styles.fieldLabel,
                                input: styles.fieldInput,
                            }}
                            label={formatMessage(messages.emailLabel)}
                            type="email"
                            placeholder={formatMessage(messages.emailPlaceholder)}
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); }}
                            size="md"
                            required
                        />
                        <TextInput
                            id="name"
                            classNames={{
                                root: styles.fieldRoot,
                                label: styles.fieldLabel,
                                input: styles.fieldInput,
                            }}
                            label={formatMessage(messages.firstName)}
                            placeholder={formatMessage(messages.firstNamePlaceholder)}
                            value={firstName}
                            onChange={(e) => { setFirstName(e.target.value); }}
                            size="md"
                            required
                        />
                        <TextInput
                            id="surname"
                            classNames={{
                                root: styles.fieldRoot,
                                label: styles.fieldLabel,
                                input: styles.fieldInput,
                            }}
                            label={formatMessage(messages.lastName)}
                            placeholder={formatMessage(messages.lastNamePlaceholder)}
                            value={lastName}
                            onChange={(e) => { setLastName(e.target.value); }}
                            size="md"
                            required
                        />
                        <PasswordInput
                            id="password"
                            classNames={{
                                root: styles.fieldRoot,
                                label: styles.fieldLabel,
                                input: styles.fieldInput,
                                innerInput: styles.passwordInnerInput,
                                section: styles.passwordSection,
                            }}
                            label={(
                                <span className={styles.labelWithTooltip}>
                                    <span>{formatMessage(messages.passwordLabel)}</span>
                                    <Toggletip
                                        align="bottom"
                                    >
                                        <ToggletipButton
                                            label={formatMessage(messages.passwordTooltipAriaLabel)}
                                        >
                                            <Information size={16} />
                                        </ToggletipButton>
                                        <ToggletipContent>
                                            <div className={styles.passwordToggletipBody}>
                                                <p>{formatMessage(messages.passwordRequirementsIntro)}</p>
                                                <ul>
                                                    {passwordRequirements.map((requirement) => (
                                                        <li key={requirement.key}>{requirement.label}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </ToggletipContent>
                                    </Toggletip>
                                </span>
                            )}
                            placeholder={formatMessage(messages.passwordPlaceholder)}
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); }}
                            error={shouldShowPasswordValidation ? passwordValidationMessage : undefined}
                            size="md"
                            required
                        />

                        <PasswordInput
                            id="confirmPassword"
                            classNames={{
                                root: styles.fieldRoot,
                                label: styles.fieldLabel,
                                input: styles.fieldInput,
                                innerInput: styles.passwordInnerInput,
                                section: styles.passwordSection,
                            }}
                            label={formatMessage(messages.confirmPasswordLabel)}
                            placeholder={formatMessage(messages.confirmPasswordPlaceholder)}
                            value={confirmPassword}
                            onChange={(e) => { setConfirmPassword(e.target.value); }}
                            error={shouldShowConfirmPasswordValidation
                                ? formatMessage(messages.passwordMismatch)
                                : undefined}
                            size="md"
                            required
                        />

                        <Button
                            type="submit"
                            disabled={isSubmitDisabled}
                            className={styles.submitButton}
                            loading={isLoading}
                            loaderProps={{ type: 'dots' }}
                        >
                            {isLoading
                                ? formatMessage(messages.creatingAccountButton)
                                : formatMessage(messages.createAccountButton)}
                        </Button>

                        <div className={styles.centerText}>
                            <p className={styles.signIn}>
                                {formatMessage(messages.alreadyHaveAccount)}
                                {' '}
                                <Link
                                    size="sm"
                                    onClick={() => { navigate('/login'); }}
                                >
                                    {formatMessage(messages.signInButton)}
                                </Link>
                            </p>
                        </div>
                    </Stack>
                </form>
            </Stack>
        </div>
    );
};

const messages = defineMessages({
    createAccountTitle: {
        id: 'src.Screens.Register.RegisterScreen.createAccountTitle',
        defaultMessage: 'Utwórz konto',
    },
    accountCreationFailed: {
        id: 'src.Screens.Register.RegisterScreen.accountCreationFailed',
        defaultMessage: 'Nie udało się utworzyć konta',
    },
    createAccountSubtitle: {
        id: 'src.Screens.Register.RegisterScreen.createAccountSubtitle',
        defaultMessage: 'Zarejestruj się, aby rozpocząć',
    },
    errorTitle: {
        id: 'src.Screens.Register.RegisterScreen.errorTitle',
        defaultMessage: 'Błąd',
    },
    passwordMismatch: {
        id: 'src.Screens.Register.RegisterScreen.passwordMismatch',
        defaultMessage: 'Hasła nie są identyczne',
    },
    passwordRequirementsIntro: {
        id: 'src.Screens.Register.RegisterScreen.passwordRequirementsIntro',
        defaultMessage: 'Hasło będzie poprawne, jeśli spełni wszystkie te warunki:',
    },
    passwordRequirementMinLength: {
        id: 'src.Screens.Register.RegisterScreen.passwordRequirementMinLength',
        defaultMessage: 'ma minimum 6 znaków',
    },
    passwordRequirementDigit: {
        id: 'src.Screens.Register.RegisterScreen.passwordRequirementDigit',
        defaultMessage: 'ma przynajmniej 1 cyfrę, np. 7',
    },
    passwordRequirementLowercase: {
        id: 'src.Screens.Register.RegisterScreen.passwordRequirementLowercase',
        defaultMessage: 'ma przynajmniej 1 małą literę, np. a',
    },
    passwordRequirementUppercase: {
        id: 'src.Screens.Register.RegisterScreen.passwordRequirementUppercase',
        defaultMessage: 'ma przynajmniej 1 dużą literę, np. A',
    },
    passwordRequirementsError: {
        id: 'src.Screens.Register.RegisterScreen.passwordRequirementsError',
        defaultMessage: 'Hasło jest jeszcze za słabe. Brakuje tego: {requirements}.',
    },
    passwordTooltipAriaLabel: {
        id: 'src.Screens.Register.RegisterScreen.passwordTooltipAriaLabel',
        defaultMessage: 'Pokaż wymagania dotyczące hasła',
    },
    incorrectFirstName: {
        id: 'src.Screens.Register.RegisterScreen.incorrectFirstName',
        defaultMessage: 'Wprowadź poprawne imię',
    },
    incorrectLastName: {
        id: 'src.Screens.Register.RegisterScreen.incorrectLastName',
        defaultMessage: 'Wprowadź poprawne nazwisko',
    },
    emailLabel: {
        id: 'src.Screens.Register.RegisterScreen.emailLabel',
        defaultMessage: 'Adres e-mail',
    },
    firstName: {
        id: 'src.Screens.Register.RegisterScreen.firstName',
        defaultMessage: 'Imię',
    },
    lastName: {
        id: 'src.Screens.Register.RegisterScreen.lastName',
        defaultMessage: 'Nazwisko',
    },
    emailPlaceholder: {
        id: 'src.Screens.Register.RegisterScreen.emailPlaceholder',
        defaultMessage: 'Wprowadź swój e-mail',
    },
    firstNamePlaceholder: {
        id: 'src.Screens.Register.RegisterScreen.firstNamePlaceholder',
        defaultMessage: 'Wprowadź swoje imię',
    },
    lastNamePlaceholder: {
        id: 'src.Screens.Register.RegisterScreen.lastNamePlaceholder',
        defaultMessage: 'Wprowadź swoje nazwisko',
    },
    passwordLabel: {
        id: 'src.Screens.Register.RegisterScreen.passwordLabel',
        defaultMessage: 'Hasło',
    },
    passwordPlaceholder: {
        id: 'src.Screens.Register.RegisterScreen.passwordPlaceholder',
        defaultMessage: 'Wprowadź swoje hasło',
    },
    confirmPasswordLabel: {
        id: 'src.Screens.Register.RegisterScreen.confirmPasswordLabel',
        defaultMessage: 'Potwierdź hasło',
    },
    confirmPasswordPlaceholder: {
        id: 'src.Screens.Register.RegisterScreen.confirmPasswordPlaceholder',
        defaultMessage: 'Potwierdź swoje hasło',
    },
    createAccountButton: {
        id: 'src.Screens.Register.RegisterScreen.createAccountButton',
        defaultMessage: 'Utwórz konto',
    },
    creatingAccountButton: {
        id: 'src.Screens.Register.RegisterScreen.creatingAccountButton',
        defaultMessage: 'Tworzenie konta...',
    },
    alreadyHaveAccount: {
        id: 'src.Screens.Register.RegisterScreen.alreadyHaveAccount',
        defaultMessage: 'Masz już konto?',
    },
    signInButton: {
        id: 'src.Screens.Register.RegisterScreen.signInButton',
        defaultMessage: 'Zaloguj się',
    },
});

export default RegisterScreen;
